<template lang="pug">
  .return-details
    Row
      Column(tablet="4" desktop="3")
        RogueSelect.form-field(
          :class="{ 'input-error': selectedOptions.returnType === 'return' && product.unreturnableMessage }"
          :error="selectedOptions.returnType === 'return' && __(product.unreturnableMessage) || null"
          label="Return or Exchange"
          :options="returnTypes"
          required
          v-model="selectedOptions.returnType"
          validate-fn="validateType"
          @update:modelValue="onValueChanged"
        )
      Column(tablet="4" desktop="3")
        RogueSelect.form-field(
          label="Reason for Return"
          :options="reasons[selectedOptions.returnType]"
          :disabled-option-text="__('Choose an option')"
          required
          v-model="selectedOptions.returnReason"
          @update:modelValue="onValueChanged"
        )
      Column(tablet="4" desktop="3" v-if="product.qtyReturnable > 1")
        .field.form-field
          span.block.label {{ __('Quantity to Return') }}
          Stepper(
            :max="product.qtyReturnable"
            v-model="selectedOptions.qty"
            @update:modelValue="onValueChanged"
          )
    Row(v-if="selectedOptions.returnType === 'exchange'")
      Column(tablet="8" desktop="6")
        RogueInput.form-field(
          label="Item to Exchange For"
          required
          v-model="selectedOptions.returnComment"
          @update:modelValue="onValueChanged"
        )
</template>

<script setup lang="ts">
import { type PropType, reactive, onMounted } from 'vue'
import type { OrderedProduct, ReturningProduct } from '../types'
import { ReturnReasons, ExchangeReasons } from '../helpers'

const emit = defineEmits<{
  (e: 'input', value: { returnType: string; returnReason: string; qty: number; returnComment: string }): void
}>()

const props = defineProps({
  product       : { type: Object as PropType<OrderedProduct>, required: true },
  selectedItems : { type: Array as PropType<ReturningProduct[]>, required: true },
})

const selectedOptions = reactive({
  returnType    : 'return',
  returnReason  : '',
  qty           : 1,
  returnComment : '',
})

const returnTypes = [
  { label: 'Return', value: 'return' },
  { label: 'Exchange', value: 'exchange' },
]

const reasons = reactive<{ return: { value: string; label: string }[]; exchange: { value: string; label: string }[] }>({
  return   : [],
  exchange : [],
})

function load() {
  reasons.return = Object.entries(ReturnReasons as Record<string, string>).map(([value, label]) => ({ value, label }))
  reasons.exchange = Object.entries(ExchangeReasons as Record<string, string>).map(([value, label]) => ({ value, label }))
  const { returnType, returnReason, qty, returnComment } = props.selectedItems.find(item => item.sku === props.product.sku) || ({} as ReturningProduct)
  selectedOptions.returnType = returnType || (!props.product.unreturnableMessage ? 'return' : 'exchange')
  selectedOptions.returnReason = returnReason || ''
  selectedOptions.qty = qty || 1
  selectedOptions.returnComment = returnComment || ''
}

function validateType() {
  if (selectedOptions.returnType === 'return' && props.product.unreturnableMessage) {
    return props.product.unreturnableMessage
  }

  return null
}

function onValueChanged() {
  emit('input', selectedOptions)
}

onMounted(load)
</script>

<style lang="scss" scoped>
.field:deep() {
  .decrement, .increment, .input-wrap input {
    height: 44px;
  }

  .required {
    display: none;
  }
}

@include mobile {
  .row-wrap:last-child .column:last-child .form-field {
    margin-bottom: 0;
  }
}

@include not-mobile {
  .form-field {
    margin-bottom: $space-s;
  }
}
</style>
